import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';

const QuestionModal = ({ show, handleClose, onSave, selectedQuestion, notifications = [], handleCloseNotification }) => {
  const [formData, setFormData] = useState({
    questionText: '',
    options: ['', '', '', ''],
    correctAnswer: '',
    section: '',
    difficulty: 'easy',
  });

  const [errors, setErrors] = useState({});
  const [sections, setSections] = useState([]);
  const [sectionSearch, setSectionSearch] = useState('');
  const [isSectionDropdownOpen, setIsSectionDropdownOpen] = useState(false);
  const [newSection, setNewSection] = useState('');
  const [isAddingSection, setIsAddingSection] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localNotifications, setLocalNotifications] = useState([]);
  const [delayedNotifications, setDelayedNotifications] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isCorrectAnswerDropdownOpen, setIsCorrectAnswerDropdownOpen] = useState(false);
  const [correctAnswerDropdownPosition, setCorrectAnswerDropdownPosition] = useState('bottom');
  const [sectionDropdownPosition, setSectionDropdownPosition] = useState('top');
  const [isDifficultyDropdownOpen, setIsDifficultyDropdownOpen] = useState(false);
  const [difficultyDropdownPosition, setDifficultyDropdownPosition] = useState('top');

  const hasFetchedSections = useRef(false);
  const correctAnswerDropdownRef = useRef(null);
  const sectionDropdownRef = useRef(null);
  const difficultyDropdownRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const updateDropdownPosition = (dropdownRef, setPosition) => {
    if (!dropdownRef.current || dropdownRef === sectionDropdownRef || dropdownRef === difficultyDropdownRef) return;
    const rect = dropdownRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = 150;
    setPosition(spaceBelow < dropdownHeight ? 'top' : 'bottom');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (correctAnswerDropdownRef.current && !correctAnswerDropdownRef.current.contains(event.target)) {
        setIsCorrectAnswerDropdownOpen(false);
      }
      if (sectionDropdownRef.current && !sectionDropdownRef.current.contains(event.target)) {
        setIsSectionDropdownOpen(false);
        setIsAddingSection(false);
      }
      if (difficultyDropdownRef.current && !difficultyDropdownRef.current.contains(event.target)) {
        setIsDifficultyDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isCorrectAnswerDropdownOpen) {
      updateDropdownPosition(correctAnswerDropdownRef, setCorrectAnswerDropdownPosition);
    }
  }, [isCorrectAnswerDropdownOpen]);

  useEffect(() => {
    if (show) {
      hasFetchedSections.current = false;
      fetchSections();
    }
  }, [show]);

  useEffect(() => {
    if (selectedQuestion) {
      setFormData({
        questionText: selectedQuestion.questionText || selectedQuestion.text || '',
        options: selectedQuestion.options || ['', '', '', ''],
        correctAnswer: selectedQuestion.correctAnswer || '',
        section: selectedQuestion.section || selectedQuestion.part || '',
        difficulty: selectedQuestion.difficulty || 'easy',
      });
      setSectionSearch(selectedQuestion.section || selectedQuestion.part || '');
      setErrors({});
    } else {
      setFormData({
        questionText: '',
        options: ['', '', '', ''],
        correctAnswer: '',
        section: '',
        difficulty: 'easy',
      });
      setSectionSearch('');
      setErrors({});
    }
    setLocalNotifications([]);
    setDelayedNotifications([]);
    setIsSaving(false);
  }, [selectedQuestion, show]);

  useEffect(() => {
    if (notifications.length > 0) {
      const timers = notifications.map((notification) => {
        return setTimeout(() => {
          setDelayedNotifications((prev) => [...prev, notification]);
        }, 1000);
      });

      const closeTimers = notifications.map((notification) =>
        setTimeout(() => {
          handleCloseNotification(notification.id);
          setDelayedNotifications((prev) => prev.filter((n) => n.id !== notification.id));
        }, 6000)
      );

      return () => {
        timers.forEach((timer) => clearTimeout(timer));
        closeTimers.forEach((timer) => clearTimeout(timer));
      };
    }
  }, [notifications, handleCloseNotification]);

  useEffect(() => {
    if (localNotifications.length > 0) {
      const timers = localNotifications.map((notification, index) =>
        setTimeout(() => {
          setLocalNotifications((prev) => prev.filter((_, i) => i !== index));
        }, 5000)
      );
      return () => timers.forEach((timer) => clearTimeout(timer));
    }
  }, [localNotifications]);

  const showLocalNotification = (message) => {
    const newNotification = { message, id: Date.now() };
    setLocalNotifications((prev) => [...prev, newNotification]);
  };

  const handleLocalCloseNotification = (id) => {
    setLocalNotifications((prev) => prev.filter((notification) => notification.id !== id));
  };

  const fetchSections = async () => {
    if (hasFetchedSections.current) return;
    hasFetchedSections.current = true;
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/sections`);
      if (!response.ok) {
        throw new Error(`Failed to fetch sections: ${response.statusText}`);
      }
      const text = await response.text();
      if (!text) {
        throw new Error('Empty response from server');
      }
      const data = JSON.parse(text);
      setSections(data);
    } catch (error) {
      console.error('Error fetching sections:', error);
      showLocalNotification(`Error fetching sections: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSection = async () => {
    if (!newSection.trim()) {
      showLocalNotification('Please enter a section name');
      return;
    }

    const existingSection = sections.find(
      (section) => section.name.toLowerCase() === newSection.toLowerCase()
    );
    if (existingSection) {
      showLocalNotification('Section already exists');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/sections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: newSection.trim() }),
      });

      if (!response.ok) {
        const text = await response.text();
        let errorMessage = 'Failed to create section';
        try {
          const errorData = JSON.parse(text);
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          errorMessage = text || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const newSectionData = await response.json();
      setSections([...sections, newSectionData]);
      setFormData({ ...formData, section: newSectionData.name });
      setSectionSearch(newSectionData.name);
      setErrors((prev) => ({ ...prev, section: '', newSection: '' }));
      setNewSection('');
      setIsAddingSection(false);
      setIsSectionDropdownOpen(false);
      showLocalNotification('Section added successfully');
    } catch (error) {
      console.error('Error adding section:', error);
      showLocalNotification(`Failed to add section: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleCorrectAnswerSelect = (option) => {
    setFormData({ ...formData, correctAnswer: option });
    setErrors((prev) => ({ ...prev, correctAnswer: '' }));
    setIsCorrectAnswerDropdownOpen(false);
  };

  const handleSectionSelect = (section) => {
    setFormData({ ...formData, section: section.name });
    setSectionSearch(section.name);
    setErrors((prev) => ({ ...prev, section: '' }));
    setIsSectionDropdownOpen(false);
    setIsAddingSection(false);
    setNewSection('');
  };

  const handleDifficultySelect = (difficulty) => {
    setFormData({ ...formData, difficulty });
    setErrors((prev) => ({ ...prev, difficulty: '' }));
    setIsDifficultyDropdownOpen(false);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.questionText.trim()) {
      newErrors.questionText = 'Question text is required';
    }
    const nonEmptyOptions = formData.options.filter((opt) => opt.trim() !== '');
    if (nonEmptyOptions.length < 2) {
      newErrors.options = 'Please provide at least 2 options';
    }
    if (!formData.correctAnswer || !nonEmptyOptions.includes(formData.correctAnswer)) {
      newErrors.correctAnswer = 'Please select a valid correct answer from the options';
    }
    if (!formData.section) {
      newErrors.section = 'Section is required';
    }
    if (!formData.difficulty) {
      newErrors.difficulty = 'Difficulty is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSaving(true);

    const nonEmptyOptions = formData.options.filter((opt) => opt.trim() !== '');

    const data = {
      questionText: formData.questionText.trim(),
      options: nonEmptyOptions,
      correctAnswer: formData.correctAnswer.trim(),
      section: formData.section,
      difficulty: formData.difficulty,
    };

    try {
      await onSave(data);
    } catch (error) {
      setIsSaving(false);
    }
  };

  const handleModalClose = () => {
    setIsSaving(false);
    setDelayedNotifications([]);
    handleClose();
  };

  const addOption = () => {
    if (formData.options.length < 6) {
      setFormData({ ...formData, options: [...formData.options, ''] });
    }
  };

  const removeOption = (index) => {
    if (formData.options.length > 1) {
      const newOptions = [...formData.options];
      newOptions.splice(index, 1);

      const removedOption = formData.options[index];
      const newCorrectAnswer = formData.correctAnswer === removedOption ? '' : formData.correctAnswer;

      setFormData({
        ...formData,
        options: newOptions,
        correctAnswer: newCorrectAnswer,
      });
      setErrors((prev) => ({ ...prev, options: '' }));
    }
  };

  const handleCorrectAnswerKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsCorrectAnswerDropdownOpen(!isCorrectAnswerDropdownOpen);
    }
  };

  const handleSectionInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsSectionDropdownOpen(!isSectionDropdownOpen);
    }
  };

  const handleSectionIconKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsSectionDropdownOpen(!isSectionDropdownOpen);
    }
  };

  const handleDifficultyKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsDifficultyDropdownOpen(!isDifficultyDropdownOpen);
    }
  };

  const handleDifficultyOptionKeyDown = (e, difficulty) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleDifficultySelect(difficulty);
    }
  };

  const handleOptionKeyDown = (e, option) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCorrectAnswerSelect(option);
    }
  };

  const styles = {
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '0.5rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      color: '#1F2937',
      fontFamily: "'Roboto', sans-serif",
      width: '95vw',
      maxWidth: '900px',
      margin: '0 auto',
      position: 'relative',
      maxHeight: '85vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    modalHeader: {
      borderBottom: 'none',
      padding: '0.50rem 1rem',
      marginLeft: '0.5rem',
      fontFamily: "'Roboto', sans-serif",
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    modalTitle: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: '#6B21A8',
      fontFamily: "'Roboto', sans-serif",
      margin: 0,
      flex: '1 1 auto',
    },
    headerNotification: {
      fontSize: '0.85rem',
      color: '#6B21A8',
      fontFamily: "'Roboto', sans-serif",
      fontWeight: '400',
      backgroundColor: '#F3E8FF',
      padding: '5px 10px',
      borderRadius: '4px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      animation: 'slideInFromRight 1s ease-out',
      wordBreak: 'break-word',
      overflowX: 'hidden',
      maxWidth: '50%',
      flexShrink: 0,
    },
    modalBody: {
      padding: '0 1.5rem 1.5rem 1.5rem',
      flex: '1 1 auto',
      overflowY: 'hidden',
      position: 'relative',
      fontFamily: "'Roboto', sans-serif",
      scrollbarWidth: 'thin',
    },
    scrollableContent: {
      overflowY: 'auto',
      maxHeight: 'calc(85vh - 150px)',
      paddingRight: '8px',
      fontFamily: "'Roboto', sans-serif",
    },
    formLabel: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#6B21A8',
      marginBottom: '0.5rem',
      fontFamily: "'Roboto', sans-serif",
    },
    formControl: (hasCrossIcon) => ({
      backgroundColor: '#ffffff',
      color: '#1F2937',
      border: '1px solid #D1D5DB',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      padding: hasCrossIcon ? '0.5rem 2.5rem 0.5rem 0.5rem' : '0.5rem',
      width: '100%',
      fontFamily: "'Roboto', sans-serif",
    }),
    formSelect: {
      backgroundColor: '#ffffff',
      color: '#1F2937',
      border: '1px solid #D1D5DB',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      padding: '0.5rem',
      fontFamily: "'Roboto', sans-serif",
    },
    optionsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '0.5rem',
      marginBottom: '1rem',
      fontFamily: "'Roboto', sans-serif",
    },
    optionRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      marginBottom: '0.5rem',
      fontFamily: "'Roboto', sans-serif",
    },
    optionInputContainer: {
      position: 'relative',
      width: '100%',
      fontFamily: "'Roboto', sans-serif",
    },
    sectionDifficultyGrid: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap',
      fontFamily: "'Roboto', sans-serif",
    },
    sectionField: {
      flex: '1',
      minWidth: '200px',
      fontFamily: "'Roboto', sans-serif",
    },
    difficultyField: {
      flex: '1',
      minWidth: '200px',
      fontFamily: "'Roboto', sans-serif",
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '5px 15px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#6B21A8',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      cursor: 'pointer',
      borderRadius: '30px',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      transition: 'all 0.4s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    cancelButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '5px 15px',
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151',
      cursor: 'pointer',
      borderRadius: '30px',
      border: '2px solid transparent',
      backgroundColor: '#D1D5DB',
      transition: 'all 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    addOptionButton: {
      fontSize: '0.875rem',
      padding: '0.5rem 1rem',
      fontFamily: "'Roboto', sans-serif",
    },
    dropdownContainer: {
      position: 'relative',
      fontFamily: "'Roboto', sans-serif",
    },
    correctAnswerDropdownContainer: {
      position: 'relative',
      width: '100%',
      minWidth: '100%',
      fontFamily: "'Roboto', sans-serif",
    },
    dropdownWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '0',
      fontFamily: "'Roboto', sans-serif",
    },
    dropdownButton: {
      backgroundColor: '#ffffff',
      color: '#1F2937',
      border: '1px solid #D1D5DB',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      padding: '0.5rem 3rem 0.5rem 0.75rem',
      width: '100%',
      textAlign: 'left',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'relative',
      fontFamily: "'Roboto', sans-serif",
    },
    dropdownMenu: (position) => ({
      position: 'absolute',
      [position === 'top' ? 'bottom' : 'top']: position === 'top' ? '100%' : '100%',
      [position === 'top' ? 'top' : 'bottom']: 'auto',
      left: 0,
      width: '100%',
      maxHeight: '150px',
      overflowY: 'auto',
      border: '1px solid #D1D5DB',
      borderRadius: '5px',
      backgroundColor: '#fff',
      zIndex: 10,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      padding: '0.5rem',
      marginBottom: position === 'top' ? 'var(--space-xs, 8px)' : '0',
      marginTop: position === 'bottom' ? '0.5rem' : '0',
      fontFamily: "'Roboto', sans-serif",
    }),
    dropdownItem: {
      padding: '0.5rem',
      cursor: 'pointer',
      borderRadius: '0.375rem',
      transition: 'background-color 0.3s ease',
      fontSize: '0.875rem',
      color: '#374151',
      whiteSpace: 'normal',
      overflowWrap: 'break-word',
      fontFamily: "'Roboto', sans-serif",
    },
    searchInputContainer: {
      position: 'relative',
      width: '100%',
      fontFamily: "'Roboto', sans-serif",
    },
    searchInput: {
      padding: '8px 40px 8px 12px',
      borderRadius: '5px 0 0 5px',
      border: '1px solid #D1D5DB',
      borderRight: 'none',
      width: '100%',
      fontSize: '0.875rem',
      boxSizing: 'border-box',
      fontFamily: "'Roboto', sans-serif",
    },
    chevronIcon: {
      position: 'absolute',
      right: '10px',
      top: '50%',
      transform: 'translateY(-50%)',
      border: 'none',
      backgroundColor: 'transparent',
      padding: '0',
      outline: 'none',
      boxShadow: 'none',
    },
    plusIcon: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '39px',
      borderRadius: '0 5px 5px 0',
      border: '1px solid #D1D5DB',
      borderLeft: 'none',
      backgroundColor: '#C5A8FF',
      color: '#fff',
      fontSize: '1.5rem',
      cursor: 'pointer',
      transition: 'background-color 0.3s ease',
      lineHeight: '2',
      textAlign: 'center',
      fontFamily: "'Roboto', sans-serif",
    },
    newSectionContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '1.1rem',
      backgroundColor: '#F9FAFB',
      borderRadius: '0.375rem',
      fontFamily: "'Roboto', sans-serif",
    },
    newSectionInput: {
      flex: '1',
      padding: '0.5rem',
      border: '1px solid #D1D5DB',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontFamily: "'Roboto', sans-serif",
    },
    tickButton: {
      backgroundColor: '#6B21A8',
      color: '#fff',
      border: 'none',
      borderRadius: '0.375rem',
      padding: '0.5rem',
      cursor: 'pointer',
      transition: 'background-color 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    notificationContainer: {
      position: 'absolute',
      top: '20px',
      right: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      width: '90%',
      maxWidth: '300px',
      zIndex: 1000,
      fontFamily: "'Roboto', sans-serif",
    },
    notification: {
      fontSize: '0.85rem',
      color: '#6B21A8',
      fontFamily: "'Roboto', sans-serif",
      fontWeight: '400',
      backgroundColor: '#F3E8FF',
      padding: '5px 10px',
      borderRadius: '4px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      animation: 'slideInFromRight 1s ease-out',
      wordBreak: 'break-word',
      overflowX: 'hidden',
    },
    notificationClose: {
      cursor: 'pointer',
      fontSize: '0.75rem',
      color: '#6B21A8',
      fontFamily: "'Roboto', sans-serif",
    },
    modalFooter: {
      borderTop: 'none',
      padding: '1px 1.5rem 5px 1.5rem',
      flexShrink: '0',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.5rem',
      backgroundColor: 'white',
      fontFamily: "'Roboto', sans-serif",
      position: 'sticky',
      bottom: 0,
      zIndex: 1,
      boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.05)',
    },
  };

  const optionsPerRow = Math.ceil(formData.options.length / 2);
  const firstRowOptions = formData.options.slice(0, optionsPerRow);
  const secondRowOptions = formData.options.slice(optionsPerRow);

  const filteredSections = sections.filter((section) =>
    section.name.toLowerCase().includes(sectionSearch.toLowerCase())
  );

  const filteredOptions = formData.options.filter((opt) => opt.trim() !== '');

  const difficultyOptions = ['easy', 'medium', 'hard'];

  return (
    <>
      <style>
        {`
          @keyframes slideInFromRight {
            0% {
              transform: translateX(100%);
              opacity: 0;
            }
            100% {
              transform: translateX(0);
              opacity: 1;
            }
          }
          .custom-button:hover:not(:disabled) {
            background: linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF);
            transform: scale(1.05);
            animation: glow 1.5s ease-in-out infinite;
          }
          .cancel-button:hover:not(:disabled) {
            background-color: #B0BEC5;
          }
          .input-field:focus {
            border-color: #6B21A8;
            outline: none;
            box-shadow: 0 0 5px rgba(107, 33, 168, 0.5);
          }
          .dropdown-button:focus {
            border-color: #6B21A8;
            outline: none;
            box-shadow: 0 0 5px rgba(107, 33, 168, 0.5);
          }
          .dropdown-item:hover {
            background-color: #F3F4F6;
          }
          .plus-icon:hover:not([aria-disabled="true"]) {
            background-color: #A78BFA;
          }
          .tick-button:hover:not(:disabled) {
            background-color: #5B1A8F;
          }
          .close-icon:hover:not([aria-disabled="true"]) {
            color: #b02a37;
            transform: scale(1.2);
            transition: color 0.3s ease, transform 0.3s ease;
          }
          .chevron-icon {
            border: none !important;
            background: transparent !important;
            outline: none !important;
            box-shadow: none !important;
          }
          .dropdown-menu::-webkit-scrollbar {
            width: 8px;
          }
          .dropdown-menu::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
          }
          .dropdown-menu::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 4px;
          }
          .dropdown-menu::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
          .modal-body::-webkit-scrollbar {
            width: 8px;
          }
          .modal-body::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
          }
          .modal-body::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 4px;
          }
          .modal-body::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
          .scrollable-content::-webkit-scrollbar {
            width: 8px;
          }
          .scrollable-content::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
          }
          .scrollable-content::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 4px;
          }
          .scrollable-content::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
          @media (max-width: 768px) {
            .modal-content {
              width: 95vw !important;
              max-width: none !important;
            }
            .modal-body {
              padding: 0 1rem 1.5rem 1rem;
            }
            .modal-footer {
              padding: 1px 1rem 5px 1rem;
            }
            .notification-container {
              width: 80% !important;
              right: 10px;
            }
          }
          @media (max-width: 576px) {
            .section-difficulty-grid {
              flex-direction: column;
            }
            .section-field,
            .difficulty-field {
              min-width: 100%;
            }
          }
        `}
      </style>
      <Modal show={show} onHide={handleModalClose} size="lg" centered>
        <div style={styles.modalContent} className="modal-content">
          <Form onSubmit={handleSubmit}>
            <Modal.Header closeButton={false} style={styles.modalHeader}>
              <Modal.Title style={styles.modalTitle}>
                {selectedQuestion ? 'Edit Question' : 'Add New Question'}
              </Modal.Title>
              {localNotifications.length > 0 && localNotifications.map((notification) => (
                notification.message === 'Section added successfully' && (
                  <div key={notification.id} style={styles.headerNotification}>
                    {notification.message}
                    <FontAwesomeIcon
                      icon={faTimes}
                      style={styles.notificationClose}
                      onClick={() => handleLocalCloseNotification(notification.id)}
                    />
                  </div>
                )
              ))}
            </Modal.Header>
            <Modal.Body style={styles.modalBody}>
              <div style={styles.scrollableContent} className="scrollable-content">
                <Form.Group className="mb-3">
                  <Form.Label style={styles.formLabel}>Question Text</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="questionText"
                    value={formData.questionText}
                    onChange={handleInputChange}
                    required
                    style={styles.formControl(false)}
                    isInvalid={!!errors.questionText}
                    disabled={isSaving}
                  />
                  <Form.Control.Feedback type="invalid" style={{ fontFamily: "'Roboto', sans-serif" }}>{errors.questionText}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={styles.formLabel}>Options</Form.Label>
                  <div style={styles.optionsGrid}>
                    <div key="first-row">
                      {firstRowOptions.map((option, index) => (
                        <div key={index} style={styles.optionRow}>
                          <div style={styles.optionInputContainer}>
                            <Form.Control
                              type="text"
                              value={option}
                              onChange={(e) => handleOptionChange(index, e.target.value)}
                              placeholder={`Option ${index + 1}`}
                              required={index < 2}
                              style={styles.formControl(index >= 4)}
                              disabled={isSaving}
                            />
                            {index >= 4 && (
                              <FontAwesomeIcon
                                icon={faTimes}
                                style={{
                                  position: 'absolute',
                                  right: '10px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  color: '#dc3545',
                                  cursor: isSaving ? 'not-allowed' : 'pointer',
                                  fontSize: '1rem',
                                  border: 'none',
                                }}
                                className="close-icon"
                                onClick={() => !isSaving && removeOption(index)}
                                aria-label={`Remove option ${index + 1}`}
                                aria-disabled={isSaving}
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div key="second-row">
                      {secondRowOptions.map((option, index) => (
                        <div key={index + optionsPerRow} style={styles.optionRow}>
                          <div style={styles.optionInputContainer}>
                            <Form.Control
                              type="text"
                              value={option}
                              onChange={(e) => handleOptionChange(index + optionsPerRow, e.target.value)}
                              placeholder={`Option ${index + optionsPerRow + 1}`}
                              required={index + optionsPerRow < 2}
                              style={styles.formControl(index + optionsPerRow >= 4)}
                              disabled={isSaving}
                            />
                            {index + optionsPerRow >= 4 && (
                              <FontAwesomeIcon
                                icon={faTimes}
                                style={{
                                  position: 'absolute',
                                  right: '10px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  color: 'gray',
                                  cursor: isSaving ? 'not-allowed' : 'pointer',
                                  fontSize: '1rem',
                                  marginLeft: '0.75rem',
                                  alignSelf: 'center',
                                  border: 'none',
                                }}
                                className="close-icon"
                                onClick={() => !isSaving && removeOption(index + optionsPerRow)}
                                aria-label={`Remove option ${index + optionsPerRow + 1}`}
                                aria-disabled={isSaving}
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {errors.options && (
                    <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.5rem', fontFamily: "'Roboto', sans-serif" }}>
                      {errors.options}
                    </div>
                  )}
                  {formData.options.length < 6 && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={addOption}
                      style={styles.addOptionButton}
                      disabled={isSaving}
                    >
                      Add Option (Max 6)
                    </Button>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={styles.formLabel}>Correct Answer</Form.Label>
                  <div style={styles.correctAnswerDropdownContainer} ref={correctAnswerDropdownRef}>
                    <div
                      style={{
                        ...styles.dropdownButton,
                        cursor: isSaving ? 'not-allowed' : 'pointer',
                        opacity: isSaving ? 0.6 : 1,
                      }}
                      onClick={() => !isSaving && setIsCorrectAnswerDropdownOpen(!isCorrectAnswerDropdownOpen)}
                      onKeyDown={handleCorrectAnswerKeyDown}
                      tabIndex={0}
                      aria-expanded={isCorrectAnswerDropdownOpen}
                      aria-label="Select correct answer"
                      role="combobox"
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {formData.correctAnswer || 'Select correct answer'}
                      </span>
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#374151',
                          fontSize: '0.875rem',
                          backgroundColor: 'transparent',
                          border: 'none',
                        }}
                        onClick={() => !isSaving && setIsCorrectAnswerDropdownOpen(!isCorrectAnswerDropdownOpen)}
                      />
                    </div>
                    {isCorrectAnswerDropdownOpen && (
                      <div style={styles.dropdownMenu(correctAnswerDropdownPosition)} className="dropdown-menu">
                        {filteredOptions.length === 0 ? (
                          <div style={{ padding: '0.5rem', color: '#888', fontFamily: "'Roboto', sans-serif" }}>No options available</div>
                        ) : (
                          filteredOptions.map((option, index) => (
                            <div
                              key={index}
                              style={styles.dropdownItem}
                              className="dropdown-item"
                              onClick={() => handleCorrectAnswerSelect(option)}
                              onKeyDown={(e) => handleOptionKeyDown(e, option)}
                              tabIndex={0}
                              role="option"
                              aria-selected={formData.correctAnswer === option}
                            >
                              {option || `Option ${index + 1}`}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                    {errors.correctAnswer && (
                      <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.5rem', fontFamily: "'Roboto', sans-serif" }}>
                        {errors.correctAnswer}
                      </div>
                    )}
                  </div>
                </Form.Group>

                <div style={styles.sectionDifficultyGrid} className="section-difficulty-grid">
                  <Form.Group style={styles.sectionField} className="mb-3">
                    <Form.Label style={styles.formLabel}>Section</Form.Label>
                    <div style={styles.dropdownContainer} ref={sectionDropdownRef}>
                      <div style={styles.dropdownWrapper}>
                        <div style={styles.searchInputContainer}>
                          <input
                            type="text"
                            value={sectionSearch}
                            onChange={(e) => {
                              setSectionSearch(e.target.value);
                              if (!isSectionDropdownOpen) {
                                setIsSectionDropdownOpen(true);
                              }
                            }}
                            onFocus={() => setIsSectionDropdownOpen(true)}
                            placeholder="Search sections..."
                            style={styles.searchInput}
                            className="input-field"
                            onKeyDown={handleSectionInputKeyDown}
                            disabled={isSaving}
                            aria-label="Search sections"
                            role="combobox"
                            aria-expanded={isSectionDropdownOpen}
                          />
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            style={styles.chevronIcon}
                            className="chevron-icon"
                            onClick={() => !isSaving && setIsSectionDropdownOpen(!isSectionDropdownOpen)}
                            onKeyDown={handleSectionIconKeyDown}
                            tabIndex={0}
                            aria-label="Toggle section dropdown"
                            aria-disabled={isSaving}
                          />
                        </div>
                        <span
                          style={{
                            ...styles.plusIcon,
                            opacity: isSaving ? 0.6 : 1,
                            pointerEvents: isSaving ? 'none' : 'auto',
                          }}
                          className="plus-icon"
                          onClick={() => {
                            if (isSaving) return;
                            setIsAddingSection(true);
                            setIsSectionDropdownOpen(true);
                            setNewSection('');
                            setErrors((prev) => ({ ...prev, newSection: '' }));
                          }}
                          aria-label="Add new section"
                          aria-disabled={isSaving}
                        >
                          +
                        </span>
                      </div>
                      {isSectionDropdownOpen && (
                        <div style={styles.dropdownMenu(sectionDropdownPosition)} className="dropdown-menu">
                          {isAddingSection && (
                            <div style={styles.newSectionContainer}>
                              <input
                                type="text"
                                value={newSection}
                                onChange={(e) => setNewSection(e.target.value)}
                                placeholder="Enter new section"
                                style={styles.newSectionInput}
                                className="input-field"
                                disabled={isSaving}
                              />
                              <button
                                style={styles.tickButton}
                                onClick={handleAddSection}
                                disabled={loading || isSaving}
                                className="tick-button"
                              >
                                <FontAwesomeIcon
                                  icon={faCheck}
                                  style={{
                                    border: 'none',
                                  }}
                                />
                              </button>
                            </div>
                          )}
                          {filteredSections.length === 0 && !isAddingSection ? (
                            <div style={{ padding: '0.5rem', color: '#888', fontFamily: "'Roboto', sans-serif" }}>No sections found</div>
                          ) : (
                            filteredSections.map((section) => (
                              <div
                                key={section._id}
                                style={styles.dropdownItem}
                                className="dropdown-item"
                                onClick={() => handleSectionSelect(section)}
                              >
                                {section.name}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                      <Form.Control.Feedback
                        type="invalid"
                        style={{ display: errors.section ? 'block' : 'none', fontSize: '0.75rem', fontFamily: "'Roboto', sans-serif" }}
                      >
                        {errors.section}
                      </Form.Control.Feedback>
                    </div>
                  </Form.Group>

                  <Form.Group style={styles.difficultyField} className="mb-3">
                    <Form.Label style={styles.formLabel}>Difficulty</Form.Label>
                    <div style={styles.dropdownContainer} ref={difficultyDropdownRef}>
                      <div
                        style={{
                          ...styles.dropdownButton,
                          cursor: isSaving ? 'not-allowed' : 'pointer',
                          opacity: isSaving ? 0.6 : 1,
                        }}
                        onClick={() => !isSaving && setIsDifficultyDropdownOpen(!isDifficultyDropdownOpen)}
                        onKeyDown={handleDifficultyKeyDown}
                        tabIndex={0}
                        aria-expanded={isDifficultyDropdownOpen}
                        aria-label="Select difficulty"
                        role="combobox"
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {formData.difficulty ? formData.difficulty.charAt(0).toUpperCase() + formData.difficulty.slice(1) : 'Select difficulty'}
                        </span>
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#374151',
                            fontSize: '0.875rem',
                            backgroundColor: 'transparent',
                            border: 'none',
                          }}
                          className="chevron-icon"
                        />
                      </div>
                      {isDifficultyDropdownOpen && (
                        <div style={styles.dropdownMenu(difficultyDropdownPosition)} className="dropdown-menu">
                          {difficultyOptions.map((difficulty, index) => (
                            <div
                              key={index}
                              style={styles.dropdownItem}
                              className="dropdown-item"
                              onClick={() => handleDifficultySelect(difficulty)}
                              onKeyDown={(e) => handleDifficultyOptionKeyDown(e, difficulty)}
                              tabIndex={0}
                              role="option"
                              aria-selected={formData.difficulty === difficulty}
                            >
                              {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                            </div>
                          ))}
                        </div>
                      )}
                      <Form.Control.Feedback
                        type="invalid"
                        style={{ display: errors.difficulty ? 'block' : 'none', fontSize: '0.75rem', fontFamily: "'Roboto', sans-serif" }}
                      >
                        {errors.difficulty}
                      </Form.Control.Feedback>
                    </div>
                  </Form.Group>
                </div>

                {(delayedNotifications.length > 0) && (
                  <div style={styles.notificationContainer} className="notification-container">
                    {delayedNotifications.map((notification) => (
                      <div key={notification.id} style={styles.notification}>
                        {notification.message}
                        <FontAwesomeIcon
                          icon={faTimes}
                          style={styles.notificationClose}
                          onClick={() => {
                            handleCloseNotification(notification.id);
                            setDelayedNotifications((prev) => prev.filter((n) => n.id !== notification.id));
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Modal.Body>
            <Modal.Footer style={styles.modalFooter} className="modal-footer">
              <Button style={styles.cancelButton} onClick={handleModalClose} className="cancel-button">
                Cancel
              </Button>
              <Button type="submit" style={styles.button} className="custom-button" disabled={isSaving}>
                {selectedQuestion ? 'SAVE' : 'Save'}
              </Button>
            </Modal.Footer>
          </Form>
        </div>
      </Modal>
    </>
  );
};

export default QuestionModal;